/**
 * Medical reference data — Modifiers & Capnogram morphologies
 *
 * Modifiers are persistent pathophysiological states (toggled on/off) that
 * apply vital `deltas` and may alter drug pharmacodynamics. Pure data.
 */

// ─── Modifiers ────────────────────────────────────────────────────────────────
//
// Modifiers are persistent states the instructor toggles.  Each defines
// vital `deltas` applied when the modifier is switched ON (and reversed
// when switched OFF).

export const MODIFIERS = {
  hypovolemia: {
    name: 'Hypovolemia / Bleeding',
    shortName: 'Hypovolemia',
    description:
      'Raises HR, lowers BP & CVP. Responds to IV fluids & blood. ' +
      'Does NOT respond to phenylephrine. Epinephrine raises HR fully but BP only slightly.',
    color: '#EF5350',
    deltas: {
      HR:       +25,
      NIBP_Sys: -28,  NIBP_Dia: -14,
      ABP_Sys:  -28,  ABP_Dia:  -14,
      CVP:      -2,
    },
  },

  malignantHyperthermia: {
    name: 'Malignant Hyperthermia',
    shortName: 'Mal. Hyperthermia',
    description:
      'Life-threatening hypermetabolic crisis. Rapid EtCO₂ rise is the EARLIEST sign — ' +
      'often precedes temperature rise by minutes. Treat with Dantrolene. ' +
      '↑↑EtCO₂, ↑↑HR, ↑↑Temp, ↑↑RR (compensatory), ↑BP, ↓SpO₂.',
    color: '#FF6D00',
    deltas: {
      EtCO2:    +28,   // dramatic rise — hallmark early finding
      HR:       +35,
      Temp:     +3.2,  // 36.8 → 40.0 (hyperthermic crisis)
      RR:       +10,   // compensatory hyperventilation
      NIBP_Sys: +15,   NIBP_Dia: +8,
      ABP_Sys:  +15,   ABP_Dia:  +8,
      SpO2:     -5,
      CVP:      +1,
    },
  },

  pneumoperitoneum: {
    name: 'Pneumoperitoneum',
    shortName: 'Pneumoperitoneum',
    description:
      'CO₂ insufflation absorption during laparoscopic surgery. ' +
      'EtCO₂ rises steadily (absorbed from peritoneal cavity). ' +
      'Trendelenburg position reduces venous return. ↑EtCO₂, ↑HR, ↑BP, ↓CVP.',
    color: '#7B1FA2',
    deltas: {
      EtCO2:    +15,
      HR:       +8,
      NIBP_Sys: +8,    NIBP_Dia: +4,
      ABP_Sys:  +8,    ABP_Dia:  +4,
      CVP:      -1,    // Trendelenburg reduces venous return
    },
  },

  pulmonaryEmbolism: {
    name: 'Pulmonary Embolism',
    shortName: 'Pulm. Embolism',
    description:
      'Acute V/Q mismatch — perfusion blocked, ventilation wasted (dead space ↑↑). ' +
      'EtCO₂ drops acutely while PaCO₂ RISES (widened a-ET gradient). ' +
      'Right heart strain: CVP ↑. ↓↓EtCO₂, ↑HR, ↓BP, ↓SpO₂, ↑CVP.',
    color: '#C62828',
    deltas: {
      EtCO2:    -18,   // dead-space effect: CO₂ cannot reach exhaled gas
      HR:       +30,
      NIBP_Sys: -25,   NIBP_Dia: -12,
      ABP_Sys:  -25,   ABP_Dia:  -12,
      SpO2:     -12,
      CVP:      +4,    // right heart strain / acute cor pulmonale
    },
  },

  tensionPneumothorax: {
    name: 'Tension Pneumothorax',
    shortName: 'Tension PTX',
    description:
      'LIFE-THREATENING. Air trapped in pleural space compresses lung and mediastinum → obstructive shock. ' +
      'Classic triad: HIGH airway pressure + HYPOTENSION + DESATURATION. ' +
      'CVP ↑↑ (venous obstruction), BP ↓↓↓, HR ↑↑, SpO₂ ↓↓, EtCO₂ ↓↓. ' +
      'Treat: immediate needle decompression (2nd ICS MCL) → finger thoracostomy.',
    color: '#5C6BC0',   // indigo — cyanosis/respiratory emphasis
    // Capnogram auto-switches to obstructed: compressed airways cause a sloped,
    // rounded waveform with prolonged upstroke (loss of normal rectangular shape).
    capnoMorphology: 'obstructed',
    // Mediastinal compression → ↑↑ airway pressures, collapsed delivered VTe.
    vent: { vte: 90, ppeak: 50, pplat: 44 },
    deltas: {
      EtCO2:    -22,   // ↓↓ dead-space: reduced pulmonary blood flow
      HR:       +38,   // ↑↑ compensatory tachycardia (obstructive shock)
      NIBP_Sys: -55,   NIBP_Dia: -30,   // ↓↓↓ precipitous hypotension
      ABP_Sys:  -55,   ABP_Dia:  -30,
      SpO2:     -22,   // ↓↓ massive R-to-L shunt (blood bypasses collapsed lung)
      CVP:      +9,    // ↑↑↑ markedly elevated — SVC/IVC compression
    },
  },
};

// ─── Capnogram morphology options ─────────────────────────────────────────────
// Separate from vital-sign modifiers: these change the capnogram SHAPE,
// not the EtCO₂ number.  Maps key → display label + description.

export const CAPNO_MORPHOLOGIES = {
  normal: {
    label: 'Normal',
    description: 'Trapezoidal: flat Phase I → steep upstroke → flat plateau → rapid downstroke.',
  },
  rebreathing: {
    label: 'Rebreathing',
    description: 'Baseline elevated above zero — CO₂ in inspired gas. Exhausted absorbent or faulty valve.',
  },
  curare_cleft: {
    label: 'Curare Cleft',
    description: 'Notch in alveolar plateau — spontaneous breath against the ventilator. Paralysis wearing off.',
  },
  obstructed: {
    label: 'Shark Fin',
    description: 'Prolonged slow upstroke, no distinct plateau. Bronchospasm, COPD, or partial airway obstruction.',
  },
};
